import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { CheckBox } from 'devextreme-react/check-box';
import './MapRoutes.scss';

const MapRoutes = ({ rutasUsuarios }) => {
    const [center, setCenter] = useState({ lat: 10.47915, lng: -66.90618 });
    const [zoom, setZoom] = useState(10);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const [filtrosDias, setFiltrosDias] = useState({
        lunes: true,
        martes: true,
        miercoles: true,
        jueves: true,
        viernes: true,
        sabado: true,
        domingo: true,
    });

    const [filtrosUsuarios, setFiltrosUsuarios] = useState({});
    const [mostrarLineas, setMostrarLineas] = useState(true);

    // Colores para cada usuario (useMemo para evitar recrear el array)
    const coloresUsuarios = useMemo(() => [
        '#FF6B6B', // Rojo
        '#4ECDC4', // Turquesa
        '#45B7D1', // Azul claro
        '#FFA07A', // Salmón
        '#98D8C8', // Verde menta
        '#F7DC6F', // Amarillo
        '#BB8FCE', // Morado
        '#85C1E2', // Azul cielo
        '#F8B739', // Naranja
        '#52B788', // Verde
    ], []);

    useEffect(() => {
        if (rutasUsuarios.length === 0) return;
        const filtrosIniciales = {};
        rutasUsuarios.forEach((ruta, index) => {
            filtrosIniciales[ruta.usuario.uid] = {
                activo: true,
                color: coloresUsuarios[index % coloresUsuarios.length],
                nombre: ruta.usuario.nombre,
            };
        });
        setFiltrosUsuarios(filtrosIniciales);

        const todosEstablecimientos = rutasUsuarios.flatMap(ruta =>
            ruta.establecimientos.filter(est =>
                est.coordenadas &&
                est.coordenadas.lat &&
                est.coordenadas.lng
            )
        );

        if (todosEstablecimientos.length > 0) {
            const sumLat = todosEstablecimientos.reduce((sum, est) => sum + est.coordenadas.lat, 0);
            const sumLng = todosEstablecimientos.reduce((sum, est) => sum + est.coordenadas.lng, 0);

            setCenter({
                lat: sumLat / todosEstablecimientos.length,
                lng: sumLng / todosEstablecimientos.length,
            });
            setZoom(12);
        }
    }, [rutasUsuarios, coloresUsuarios]);

    const toggleFiltroDia = useCallback((dia, nuevoValor) => {
        setFiltrosDias(prev => ({
            ...prev,
            [dia]: nuevoValor,
        }));
    }, []);

    const toggleFiltroUsuario = useCallback((uid, nuevoValor) => {
        setFiltrosUsuarios(prev => ({
            ...prev,
            [uid]: {
                ...prev[uid],
                activo: nuevoValor,
            },
        }));
    }, []);

    const toggleTodosDias = useCallback(() => {
        const valores = Object.values(filtrosDias);
        if (valores.length === 0) return;

        const todosActivos = valores.every(v => v === true);
        const nuevoEstado = !todosActivos;

        setFiltrosDias({
            lunes: nuevoEstado,
            martes: nuevoEstado,
            miercoles: nuevoEstado,
            jueves: nuevoEstado,
            viernes: nuevoEstado,
            sabado: nuevoEstado,
            domingo: nuevoEstado,
        });
    }, [filtrosDias]);

    const toggleTodosUsuarios = useCallback(() => {
        const valores = Object.values(filtrosUsuarios);
        if (valores.length === 0) return;

        const todosActivos = valores.every(u => u && u.activo === true);
        const nuevoEstado = !todosActivos;

        setFiltrosUsuarios(prev => {
            const nuevosFiltros = { ...prev };
            Object.keys(nuevosFiltros).forEach(uid => {
                if (nuevosFiltros[uid]) {
                    nuevosFiltros[uid] = {
                        ...nuevosFiltros[uid],
                        activo: nuevoEstado,
                    };
                }
            });
            return nuevosFiltros;
        });
    }, [filtrosUsuarios]);

    // Función para calcular distancia entre dos puntos
    const calcularDistancia = useCallback((punto1, punto2) => {
        const R = 6371; // Radio de la Tierra en km
        const lat1 = punto1.coordenadas.lat * Math.PI / 180;
        const lat2 = punto2.coordenadas.lat * Math.PI / 180;
        const dLat = (punto2.coordenadas.lat - punto1.coordenadas.lat) * Math.PI / 180;
        const dLon = (punto2.coordenadas.lng - punto1.coordenadas.lng) * Math.PI / 180;

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distancia en km
    }, []);


    // Calcular distancia total de una ruta
    const calcularDistanciaTotal = useCallback((ruta) => {
        if (ruta.length < 2) return 0;

        let total = 0;
        for (let i = 0; i < ruta.length - 1; i++) {
            total += calcularDistancia(ruta[i], ruta[i + 1]);
        }
        return total;
    }, [calcularDistancia]);

    // Función para determinar si un establecimiento debe mostrarse
    const debesMostrarEstablecimiento = useCallback((establecimiento, usuarioUid) => {
        // Verificar si el usuario está activo
        if (!filtrosUsuarios[usuarioUid]?.activo) return false;

        // Verificar si el establecimiento tiene coordenadas válidas
        if (!establecimiento.coordenadas ||
            !establecimiento.coordenadas.lat ||
            !establecimiento.coordenadas.lng) {
            console.log(`Establecimiento ${establecimiento.ID} - ${establecimiento.nombre_establecimiento} NO tiene coordenadas válidas`);
            return false;
        }

        // Verificar si alguno de los días del establecimiento está activo en los filtros
        const tieneDiaActivo =
            (establecimiento.lunes && filtrosDias.lunes) ||
            (establecimiento.martes && filtrosDias.martes) ||
            (establecimiento.miercoles && filtrosDias.miercoles) ||
            (establecimiento.jueves && filtrosDias.jueves) ||
            (establecimiento.viernes && filtrosDias.viernes) ||
            (establecimiento.sabado && filtrosDias.sabado) ||
            (establecimiento.domingo && filtrosDias.domingo);

        if (!tieneDiaActivo) {
            console.log(`Establecimiento ${establecimiento.ID} - ${establecimiento.nombre_establecimiento} NO tiene días activos en los filtros`);
        }

        return tieneDiaActivo;
    }, [filtrosUsuarios, filtrosDias]);

    // Obtener los días activos de un establecimiento
    const obtenerDiasActivos = useCallback((establecimiento) => {
        const dias = [];
        if (establecimiento.lunes) dias.push('LUNES');
        if (establecimiento.martes) dias.push('MARTES');
        if (establecimiento.miercoles) dias.push('MIERCOLES');
        if (establecimiento.jueves) dias.push('JUEVES');
        if (establecimiento.viernes) dias.push('VIERNES');
        if (establecimiento.sabado) dias.push('SABADO');
        if (establecimiento.domingo) dias.push('DOMINGO');
        return dias.join('-');
    }, []);

    // Crear marcadores filtrados con useMemo
    const marcadoresFiltrados = useMemo(() => {
        const marcadores = [];

        rutasUsuarios.forEach((ruta) => {
            ruta.establecimientos.forEach((establecimiento) => {
                if (debesMostrarEstablecimiento(establecimiento, ruta.usuario.uid)) {
                    marcadores.push({
                        ...establecimiento,
                        usuario: ruta.usuario,
                        color: filtrosUsuarios[ruta.usuario.uid]?.color,
                    });
                }
            });
        });

        return marcadores;
    }, [rutasUsuarios, filtrosUsuarios, debesMostrarEstablecimiento]);

    // Crear rutas (polylines) para cada usuario por día
    const rutasPolylines = useMemo(() => {
        const rutas = [];
        const diasSemana = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

        rutasUsuarios.forEach((ruta) => {
            // Solo procesar si el usuario está activo
            if (!filtrosUsuarios[ruta.usuario.uid]?.activo) return;

            // Para cada día de la semana
            diasSemana.forEach((dia, index) => {
                // Solo procesar si el día está activo en los filtros
                if (!filtrosDias[dia]) return;

                // Filtrar establecimientos de este día con coordenadas válidas
                const establecimientosDia = ruta.establecimientos.filter(est =>
                    est.coordenadas &&
                    est.coordenadas.lat &&
                    est.coordenadas.lng &&
                    est[dia] === true
                );

                if (establecimientosDia.length >= 2) {
                    // Usar los establecimientos en el orden que vienen (sin optimizar)
                    const distanciaTotal = calcularDistanciaTotal(establecimientosDia);

                    rutas.push({
                        id: `${ruta.usuario.uid}-${dia}`,
                        usuario: ruta.usuario,
                        dia: dia,
                        path: establecimientosDia.map(est => ({
                            lat: est.coordenadas.lat,
                            lng: est.coordenadas.lng
                        })),
                        color: filtrosUsuarios[ruta.usuario.uid]?.color,
                        distancia: distanciaTotal,
                        cantidadEstablecimientos: establecimientosDia.length
                    });
                }
            });
        });

        return rutas;
    }, [rutasUsuarios, filtrosUsuarios, filtrosDias, calcularDistanciaTotal]);

    // Calcular estadísticas de distancias
    const estadisticasDistancias = useMemo(() => {
        const totalDistancia = rutasPolylines.reduce((sum, ruta) => sum + ruta.distancia, 0);
        const cantidadRutas = rutasPolylines.length;

        return {
            totalKm: totalDistancia.toFixed(2),
            promedioKm: cantidadRutas > 0 ? (totalDistancia / cantidadRutas).toFixed(2) : 0,
            cantidadRutas: cantidadRutas
        };
    }, [rutasPolylines]);

    // Crear SVG de marcador personalizado con color
    const crearIconoMarcador = useCallback((color) => {
        return {
            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
            fillColor: color,
            fillOpacity: 0.9,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 8,
        };
    }, []);

    return (
        <div className="map-routes-container">
            <div className="map-routes-content">
                {/* Panel de Filtros */}
                <div className="filters-panel">
                    {/* Filtros de Días */}
                    <div className="filter-section">
                        <div className="filter-header">
                            <h6>Filtrar por Día</h6>
                            <button
                                className="btn-toggle-all"
                                onClick={toggleTodosDias}
                            >
                                {Object.values(filtrosDias).length > 0 && Object.values(filtrosDias).every(v => v === true) ? 'Desmarcar todos' : 'Marcar todos'}
                            </button>
                        </div>
                        <div className="filter-items">
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.lunes}
                                    onValueChanged={(e) => toggleFiltroDia('lunes', e.value)}
                                    text="Lunes"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.martes}
                                    onValueChanged={(e) => toggleFiltroDia('martes', e.value)}
                                    text="Martes"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.miercoles}
                                    onValueChanged={(e) => toggleFiltroDia('miercoles', e.value)}
                                    text="Miércoles"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.jueves}
                                    onValueChanged={(e) => toggleFiltroDia('jueves', e.value)}
                                    text="Jueves"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.viernes}
                                    onValueChanged={(e) => toggleFiltroDia('viernes', e.value)}
                                    text="Viernes"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.sabado}
                                    onValueChanged={(e) => toggleFiltroDia('sabado', e.value)}
                                    text="Sábado"
                                />
                            </div>
                            <div className="filter-item">
                                <CheckBox
                                    value={filtrosDias.domingo}
                                    onValueChanged={(e) => toggleFiltroDia('domingo', e.value)}
                                    text="Domingo"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filtros de Usuarios */}
                    <div className="filter-section">
                        <div className="filter-header">
                            <h6>Filtrar por Usuario</h6>
                            <button
                                className="btn-toggle-all"
                                onClick={toggleTodosUsuarios}
                            >
                                {Object.values(filtrosUsuarios).length > 0 && Object.values(filtrosUsuarios).every(u => u && u.activo === true) ? 'Desmarcar todos' : 'Marcar todos'}
                            </button>
                        </div>
                        <div className="filter-items">
                            {Object.entries(filtrosUsuarios).map(([uid, filtro]) => (
                                <div key={uid} className="filter-item-user">
                                    <div
                                        className="color-indicator"
                                        style={{ backgroundColor: filtro.color }}
                                    ></div>
                                    <CheckBox
                                        value={filtro.activo}
                                        onValueChanged={(e) => toggleFiltroUsuario(uid, e.value)}
                                        text={filtro.nombre}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Control de líneas de ruta */}
                    <div className="filter-section">
                        <div className="filter-item">
                            <CheckBox
                                value={mostrarLineas}
                                onValueChanged={(e) => setMostrarLineas(e.value)}
                                text="Mostrar líneas de ruta"
                            />
                        </div>
                    </div>

                    {/* Estadísticas */}
                    <div className="filter-section stats-section">
                        <h6>Estadísticas</h6>
                        <div className="stats-content">
                            <p>Usuarios: {Object.values(filtrosUsuarios).filter(u => u && u.activo).length} de {rutasUsuarios.length}</p>
                            <p>Establecimientos visibles: {marcadoresFiltrados.length}</p>
                            {mostrarLineas && (
                                <>
                                    <p style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                                        <strong>Rutas:</strong>
                                    </p>
                                    <p>Cantidad de rutas: {estadisticasDistancias.cantidadRutas}</p>
                                    <p>Distancia total: {estadisticasDistancias.totalKm} km</p>
                                    <p>Distancia promedio: {estadisticasDistancias.promedioKm} km</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mapa */}
                <div className="map-container">
                    <GoogleMap
                        center={center}
                        zoom={zoom}
                        mapContainerStyle={{ height: '100%', width: '100%' }}
                        options={{
                            streetViewControl: false,
                            mapTypeControl: true,
                            fullscreenControl: true,
                        }}
                    >
                        {/* Líneas de ruta (Polylines) */}
                        {mostrarLineas && rutasPolylines.map((ruta) => (
                            <Polyline
                                key={ruta.id}
                                path={ruta.path}
                                options={{
                                    strokeColor: ruta.color,
                                    strokeOpacity: 0.7,
                                    strokeWeight: 3,
                                    geodesic: true,
                                }}
                            />
                        ))}

                        {/* Marcadores */}
                        {marcadoresFiltrados.map((marcador, index) => (
                            <Marker
                                key={`${marcador.usuario.uid}-${marcador.ID}-${index}`}
                                position={{
                                    lat: marcador.coordenadas.lat,
                                    lng: marcador.coordenadas.lng,
                                }}
                                icon={crearIconoMarcador(marcador.color)}
                                onClick={() => setSelectedMarker(marcador)}
                            />
                        ))}

                        {selectedMarker && (
                            <InfoWindow
                                position={{
                                    lat: selectedMarker.coordenadas.lat,
                                    lng: selectedMarker.coordenadas.lng,
                                }}
                                onCloseClick={() => setSelectedMarker(null)}
                            >
                                <div className="info-window-content">
                                    <h6>{selectedMarker.nombre_establecimiento}</h6>
                                    <p><strong>ID:</strong> {selectedMarker.ID}</p>
                                    <p><strong>Usuario:</strong> {selectedMarker.usuario.nombre}</p>
                                    <p><strong>Región:</strong> {selectedMarker.nombre_region}</p>
                                    <p><strong>Días:</strong> {obtenerDiasActivos(selectedMarker)}</p>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </div>
            </div>
        </div>
    );
};

export default MapRoutes;

